import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Info } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { inrCompact } from "../lib/site";
import SectionHead from "../components/site/SectionHead";

const FIELDS = [
  { key: "rate", label: "Interest Rate" },
  { key: "range", label: "Loan Amount Range" },
  { key: "tenure", label: "Tenure" },
  { key: "processing_fee", label: "Processing Fee" },
];

export default function Compare() {
  const { products } = useSite();
  const [selected, setSelected] = useState([]);

  const toggle = (slug) =>
    setSelected((s) => (s.includes(slug) ? s.filter((x) => x !== slug) : s.length < 3 ? [...s, slug] : s));

  const items = products.filter((p) => selected.includes(p.slug));

  return (
    <div data-testid="compare-page" className="container-site py-14 lg:py-20">
      <SectionHead eyebrow="Loan Comparison" title="Compare Loan Products Side by Side"
        subtitle="Select up to three products to compare indicative rates, amounts, tenure and fees." />

      <div className="flex flex-wrap gap-2.5 mt-10" data-testid="compare-selector">
        {products.map((p) => (
          <button key={p.id} onClick={() => toggle(p.slug)} data-testid={`compare-toggle-${p.slug}`}
            aria-pressed={selected.includes(p.slug)}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold border transition-colors duration-200 ${selected.includes(p.slug) ? "bg-brand-red text-white border-brand-red" : "bg-white text-brand-dark border-black/10 hover:border-black/30"}`}>
            {p.name}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="mt-10 card-site p-10 text-center text-brand-muted text-sm" data-testid="compare-empty">
          <GitCompareIcon /> Select products above to see a side-by-side comparison.
        </div>
      ) : (
        <div className="mt-10 overflow-x-auto card-site" data-testid="compare-table-wrap">
          <table className="w-full min-w-[640px] text-sm" data-testid="compare-table">
            <thead>
              <tr className="border-b border-black/5">
                <th className="text-left p-5 font-heading font-bold text-brand-dark w-44">Criteria</th>
                {items.map((p) => (
                  <th key={p.id} className="text-left p-5">
                    <Link to={`/loans/${p.slug}`} className="font-heading font-bold text-brand-dark hover:text-brand-red transition-colors duration-200">{p.name}</Link>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FIELDS.map((f) => (
                <tr key={f.key} className="border-b border-black/5 last:border-0">
                  <td className="p-5 text-brand-muted font-semibold">{f.label}</td>
                  {items.map((p) => (
                    <td key={p.id} className="p-5 text-brand-dark font-medium">
                      {f.key === "range" ? `${inrCompact(p.min_amount)} – ${inrCompact(p.max_amount)}` : p[f.key]}
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <td className="p-5" />
                {items.map((p) => (
                  <td key={p.id} className="p-5">
                    <Link to={`/apply?product=${encodeURIComponent(p.name)}`} data-testid={`compare-apply-${p.slug}`} className="btn-primary !px-5 !py-2.5 !text-xs">Apply <ArrowRight size={13} /></Link>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-6 text-xs text-brand-muted flex items-start gap-2" data-testid="compare-disclaimer">
        <Info size={14} className="mt-0.5 shrink-0 text-brand-gold" />
        Rates, fees, eligibility and other terms are subject to lender policies and customer eligibility. This comparison is indicative and does not constitute an offer.
      </p>
    </div>
  );
}

const GitCompareIcon = () => (
  <span className="block mx-auto mb-4 w-12 h-12 rounded-2xl bg-brand-cream text-brand-muted flex items-center justify-center text-lg font-bold">⇄</span>
);
